/** @jsxImportSource theme-ui @jsxRuntime classic */
import {
  Container,
  Text,
  SanityRichTextProps,
  RichTextProps,
  ImageContentsBlockProps,
  GalleryProps,
  GalleryCarousel,
  ReviewCarouselCard,
  QuoteProps,
  IntrinsicBox,
  VideoProps,
  RoxyBannerProps,
  Box,
} from '@tishman/components';
import { H } from '@hzdg/sectioning';
import React from 'react';
import { IGatsbyImageData } from 'gatsby-plugin-image';

import { getGatsbyImage } from '../utils/gatsby-source-sanity';
import { SANITY_DATASET, SANITY_PROJECT_ID } from '../environment';

import RoxyBanner from './RoxyBanner';
import { ImageContentsBlock } from './ImageContentsBlock';

const headingStyles = {
  fontFamily: 'headingSecondary',
  mt: 4,
  mb: 3,
};
const containerStyles = {
  px: ['24px', null, null, 0],
};

const StoryH1 = ({ children }: RichTextProps): JSX.Element => (
  <Container sx={containerStyles}>
    <H
      sx={{
        variant: 'styles.h1',
        ...headingStyles,
      }}
    >
      {children as unknown as string}
    </H>
  </Container>
);

const StoryH2 = ({ children }: RichTextProps): JSX.Element => (
  <Container sx={containerStyles}>
    <H
      sx={{
        variant: 'styles.h2',
        ...headingStyles,
      }}
    >
      {children as unknown as string}
    </H>
  </Container>
);

const StoryH3 = ({ children }: RichTextProps): JSX.Element => (
  <Container sx={containerStyles}>
    <H
      sx={{
        variant: 'styles.h3',
        ...headingStyles,
      }}
    >
      {children as unknown as string}
    </H>
  </Container>
);

const StoryH4 = ({ children }: RichTextProps): JSX.Element => (
  <Container sx={containerStyles}>
    <H
      sx={{
        variant: 'styles.h4',
        ...headingStyles,
      }}
    >
      {children as unknown as string}
    </H>
  </Container>
);

const StoryH5 = ({ children }: RichTextProps): JSX.Element => (
  <Container sx={containerStyles}>
    <H
      sx={{
        variant: 'styles.h5',
        ...headingStyles,
      }}
    >
      {children as unknown as string}
    </H>
  </Container>
);

const StoryP = ({ children }: RichTextProps): JSX.Element => (
  <Container sx={containerStyles}>
    <Text as="p" mb={3} variant="articleP">
      {children as unknown as string}
    </Text>
  </Container>
);

const StoryUl = ({ children }: RichTextProps): JSX.Element => (
  <Container sx={containerStyles}>
    <Box as="ul" sx={{ listStyle: 'disc', pl: 3, my: 4 }}>
      {children as unknown as string}
    </Box>
  </Container>
);

const StoryOl = ({ children }: RichTextProps): JSX.Element => (
  <Container sx={containerStyles}>
    <Box
      as="ol"
      sx={{ listStyle: 'decimal', pl: 3, my: [4, null, null, null, '92px'] }}
    >
      {children as unknown as string}
    </Box>
  </Container>
);

const StoryLi = ({ children }: RichTextProps): JSX.Element => (
  <Box as="li" sx={{ variant: 'text.mediumP' }}>
    {children as unknown as string}
  </Box>
);
const StoryImageContentsBlock = ({
  node,
}: ImageContentsBlockProps): JSX.Element => {
  const image = getGatsbyImage(
    node.photo.asset._ref,
    { maxWidth: 1024 },
    {
      projectId: SANITY_PROJECT_ID,
      dataset: SANITY_DATASET,
    },
  );

  if (!image) {
    return <div>No image available</div>;
  }

  return (
    <ImageContentsBlock
      {...node}
      align={node.photo.align}
      alt={node.photo.alt}
      blocks={node.contents}
      caption={node.photo.caption}
      image={image}
    />
  );
};

const StoryGalleryBlock = ({ node }: GalleryProps): JSX.Element => {
  const cards = node.items.map((item) => {
    const gatsbyImageData = getGatsbyImage(
      item.asset._ref,
      { maxWidth: 1024 },
      {
        projectId: SANITY_PROJECT_ID,
        dataset: SANITY_DATASET,
      },
    );

    return {
      gatsbyImageData: gatsbyImageData ?? ({} as IGatsbyImageData),
      alt: item.alt,
      caption: item.caption,
    };
  });
  return (
    <GalleryCarousel
      cards={cards}
      sx={{
        'my': [6, 8],
        'button svg': { '&:hover': { g: { stroke: 'GREEN' } } },
      }}
    />
  );
};

const StoryQuoteBlock = ({ node }: QuoteProps): JSX.Element => (
  <Container sx={{ ...containerStyles, my: [6, null, null, null, '92px'] }}>
    <ReviewCarouselCard author={node.author} quote={node.body} />
  </Container>
);

//content: string
const RoxyBannerBlock = ({ node }: RoxyBannerProps): JSX.Element => (
  <Container sx={{ ...containerStyles, maxWidth: 'unset', my: [6, 8] }}>
    <RoxyBanner text={node.text} />
  </Container>
);

/**
 * Get Vimeo ID from various Vimeo URL
 */
function getVimeoId(url: string): string {
  const vimeoRegex = /([0-9a-z\-_]+)$/i;
  return vimeoRegex.exec(url)?.[0] ?? 'Invalid vimeo id';
}

const StoryVimeoBlock = ({ node }: VideoProps): JSX.Element => {
  const vimeoId = getVimeoId(node.url);
  return (
    <IntrinsicBox ratio={1280 / 680} sx={{ my: [6, 8], position: 'relative' }}>
      <iframe
        src={`https://player.vimeo.com/video/${vimeoId}`}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
        }}
        title={`vimeo iframe ${vimeoId}`}
      />
    </IntrinsicBox>
  );
};

/**
 * Get YouTube ID from various YouTube URL
 */
function getYoutubeId(url: string): string {
  const urlArray = url
    .replace(/(>|<)/gi, '')
    .split(/(vi\/|v=|\/v\/|youtu\.be\/|\/embed\/)/);
  return urlArray[2] !== undefined
    ? urlArray[2].split(/[^0-9a-z_-]/i)[0]
    : urlArray[0];
}

const StoryYoutubeBlock = ({ node }: VideoProps): JSX.Element => {
  const youtubeId = getYoutubeId(node.url);
  return (
    <IntrinsicBox ratio={1280 / 680} sx={{ my: [6, 8], position: 'relative' }}>
      <iframe
        src={`https://www.youtube.com/embed/${youtubeId}`}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
        }}
        title={`youtube iframe ${youtubeId}`}
      />
    </IntrinsicBox>
  );
};

export const storyComponents: SanityRichTextProps['components'] = {
  h1: StoryH1,
  h2: StoryH2,
  h3: StoryH3,
  h4: StoryH4,
  h5: StoryH5,
  p: StoryP,
  ul: StoryUl,
  ol: StoryOl,
  li: StoryLi,
  imageContentsBlock: StoryImageContentsBlock,
  galleryBlock: StoryGalleryBlock,
  quoteBlock: StoryQuoteBlock,
  vimeoBlock: StoryVimeoBlock,
  youtubeBlock: StoryYoutubeBlock,
  roxyBanner: RoxyBannerBlock,
};
